import type { FormEvent } from 'react'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Box, Button, Card, CardContent, CircularProgress, Divider, TextField, Typography } from '@mui/material'
import ArticleIcon from '@mui/icons-material/Article'
import LoginIcon from '@mui/icons-material/Login'

import { useAuth } from '../hooks/useAuth'
import { authApi } from '../services/authApi'

type ApiError = {
  response?: {
    data?: {
      message?: string
    }
  }
}

export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(email, password)
      navigate('/')
    } catch (err) {
      setError((err as ApiError).response?.data?.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  function handleMicrosoftLogin(): void {
    window.location.href = authApi.microsoftStartUrl()
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ background: 'linear-gradient(135deg, #0f4c81, #1f6fb2)' }}
    >
      <Card sx={{ width: 420, p: 2 }}>
        <CardContent>
          <Box textAlign="center" mb={3}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 1.5,
                borderRadius: '50%',
                background: '#eaf3fb',
                mb: 1,
              }}
            >
              <ArticleIcon sx={{ fontSize: 32, color: '#0f4c81' }} />
            </Box>
            <Typography variant="h5" mt={1}>
              KB Portal
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to your account
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              sx={{ mb: 3 }}
            />
            <Button type="submit" variant="contained" fullWidth size="large" disabled={loading}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }}>or</Divider>

          <Button
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<LoginIcon />}
            onClick={handleMicrosoftLogin}
            disabled={loading}
          >
            Sign in with Microsoft
          </Button>
        </CardContent>
      </Card>
    </Box>
  )
}
